import Offcanvas from 'bootstrap/js/dist/offcanvas';
import ky from 'ky';
import { t } from './i18n';
import { canCheckout, clampCartQty, nextCartQty, remainingCents } from './order-rules';

interface CartItem {
  id: string;
  quantity: number;
}

interface CartResponse {
  item_count: number;
  total_price: number;
  items: CartItem[];
}

interface SectionsResponse {
  [key: string]: string;
}

class Cart {
  private cartTotalCents = 0;
  private cartItemCount = 0;
  private bypassCheckout = false;
  private checkoutPending = false;

  constructor() {
    this.init();
  }

  init(): void {
    this.bindEvents();
    this.bindCheckoutGuard();
    const initial = this.readInitialCart();
    this.applyCheckoutGate(initial.total, initial.count);
  }

  bindEvents(): void {
    // Handle quantity change buttons
    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('.qty-plus')) {
        this.handleQuantityChange(target.closest('.qty-plus') as HTMLElement, 'increase');
      } else if (target.closest('.qty-minus')) {
        this.handleQuantityChange(target.closest('.qty-minus') as HTMLElement, 'decrease');
      } else if (target.closest('.item-delete')) {
        this.handleItemRemove(target.closest('.item-delete') as HTMLElement);
      }
    });

    // Handle direct input changes
    document.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.classList.contains('form-control') && target.dataset.id) {
        this.handleDirectQuantityChange(target);
      }
    });
  }

  readInitialCart(): { total: number; count: number } {
    const totalEl = document.querySelector('[data-cart-total]');
    const countEl = document.querySelector('[data-cart-count]');
    return {
      total: Number.parseInt(totalEl?.getAttribute('data-cart-total') || '0', 10) || 0,
      count: Number.parseInt(countEl?.textContent || '0', 10) || 0,
    };
  }

  bindCheckoutGuard(): void {
    document.addEventListener('submit', (event: SubmitEvent) => {
      if (this.bypassCheckout) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (event.submitter?.getAttribute('name') !== 'checkout') return;

      event.preventDefault();
      void this.guardCheckout(form);
    });

    document.addEventListener(
      'click',
      (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const link = target.closest('a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;

        let url: URL;
        try {
          url = new URL(link.href, window.location.origin);
        } catch {
          return;
        }
        if (url.origin !== window.location.origin) return;
        if (url.pathname !== '/checkout' && !url.pathname.endsWith('/checkout')) return;
        if (canCheckout(this.cartTotalCents)) return;

        event.preventDefault();
        event.stopPropagation();
        this.applyCheckoutGate(this.cartTotalCents, this.cartItemCount);
        this.openCartDrawer();
      },
      true,
    );
  }

  async guardCheckout(form: HTMLFormElement): Promise<void> {
    if (this.checkoutPending) return;
    this.checkoutPending = true;

    try {
      const cart = (await ky.get('/cart.js').json()) as CartResponse;
      this.applyCheckoutGate(cart.total_price, cart.item_count);
      if (!canCheckout(cart.total_price)) return;
    } catch (error) {
      console.error('Error checking cart minimum:', error);
      if (!canCheckout(this.cartTotalCents)) return;
    } finally {
      this.checkoutPending = false;
    }

    this.submitCheckout(form);
  }

  submitCheckout(form: HTMLFormElement): void {
    const button = form.querySelector('[name="checkout"]');
    if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
      button.disabled = false;
    }

    this.bypassCheckout = true;
    if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
      form.requestSubmit(button);
    } else {
      form.requestSubmit();
    }
    this.bypassCheckout = false;
  }

  applyCheckoutGate(totalCents: number, itemCount: number): void {
    this.cartTotalCents = totalCents;
    this.cartItemCount = itemCount;
    const allowed = canCheckout(totalCents);
    const showNotice = itemCount > 0 && !allowed;
    const message = t('cart.minimum_remaining').replace('__AMOUNT__', this.formatMoney(remainingCents(totalCents)));

    document.querySelectorAll<HTMLButtonElement | HTMLInputElement>('[name="checkout"]').forEach((button) => {
      button.disabled = !allowed;
    });

    document.querySelectorAll<HTMLElement>('[data-cart-minimum]').forEach((notice) => {
      notice.hidden = !showNotice;
      notice.textContent = showNotice ? message : '';
    });
  }

  openCartDrawer(): void {
    const cartDrawer = document.querySelector('#offcanvas-cart');
    if (!(cartDrawer instanceof HTMLElement)) return;
    Offcanvas.getOrCreateInstance(cartDrawer).show();
  }

  formatMoney(cents: number): string {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }

  async handleQuantityChange(button: HTMLElement, action: 'increase' | 'decrease'): Promise<void> {
    const itemKey = button.dataset.id;
    if (!itemKey) return;

    const input = document.getElementById(`qty-${itemKey}`) as HTMLInputElement;
    if (!input) return;

    const currentQuantity = parseInt(input.value, 10);
    if (!Number.isFinite(currentQuantity)) return;
    const minQuantity = parseInt(input.getAttribute('min') || '0', 10) || 0;

    let newQuantity: number;
    if (action === 'increase') {
      newQuantity = nextCartQty(currentQuantity, 1, minQuantity);
    } else if (action === 'decrease') {
      newQuantity = nextCartQty(currentQuantity, -1, minQuantity);
    } else {
      return;
    }

    if (newQuantity !== currentQuantity) {
      await this.updateCartQuantity(itemKey, newQuantity, input);
    }
  }

  async handleDirectQuantityChange(input: HTMLInputElement): Promise<void> {
    const itemKey = input.dataset.id;
    if (!itemKey) return;

    const minQuantity = parseInt(input.getAttribute('min') || '0', 10) || 0;
    const newQuantity = clampCartQty(parseInt(input.value, 10), minQuantity);

    // Update input value to ensure it's valid
    input.value = newQuantity.toString();

    await this.updateCartQuantity(itemKey, newQuantity, input);
  }

  async handleItemRemove(button: HTMLElement): Promise<void> {
    const itemKey = button.dataset.removeItem;
    if (!itemKey) return;

    const input = document.getElementById(`qty-${itemKey}`) as HTMLInputElement;

    if (input) {
      await this.updateCartQuantity(itemKey, 0, input);
    }
  }

  async updateCartQuantity(itemKey: string, quantity: number, input: HTMLInputElement): Promise<void> {
    try {
      // Show loading state
      this.setLoadingState(input, true);

      // Update cart via Shopify AJAX API
      const cart: CartResponse = await ky
        .post('/cart/change.js', {
          json: {
            id: itemKey,
            quantity: quantity,
          },
        })
        .json();

      // Update the cart UI
      await this.refreshCartDisplay(cart.item_count);
      this.applyCheckoutGate(cart.total_price, cart.item_count);

      // Update input value
      input.value = quantity.toString();
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showError('Failed to update cart. Please try again.');

      // Revert input value on error
      location.reload(); // Simple fallback - reload to get correct state
    } finally {
      this.setLoadingState(input, false);
    }
  }

  setLoadingState(input: HTMLInputElement, isLoading: boolean): void {
    const container = input.closest('.product-item') as HTMLElement;
    if (!container) return;

    if (isLoading) {
      container.style.opacity = '0.6';
      container.style.pointerEvents = 'none';
    } else {
      container.style.opacity = '1';
      container.style.pointerEvents = 'auto';
    }
  }

  async refreshCartDisplay(itemCount: number): Promise<void> {
    try {
      // Fetch multiple sections in a single request for better performance
      const sections: SectionsResponse = await ky
        .get('/?sections=cart-drawer,cart-totals,cart-free-shipping', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .json();

      // Update cart items
      if (sections['cart-drawer']) {
        this.updateCartItemsFromHTML(sections['cart-drawer']);
      }

      // Update cart totals
      if (sections['cart-totals']) {
        this.updateCartTotalsFromHTML(sections['cart-totals']);
      }

      // Update free shipping banner
      if (sections['cart-free-shipping']) {
        this.updateFreeShippingFromHTML(sections['cart-free-shipping']);
      }

      // Update cart count
      await this.updateCartCount(itemCount);
    } catch (error) {
      console.error('Error refreshing cart display:', error);
      // Fallback: reload the page
      location.reload();
    }
  }

  parseHtml(html: string, selector: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const wrapperDiv = tempDiv.querySelector(selector);

    if (wrapperDiv) {
      return wrapperDiv.outerHTML;
    }

    return html;
  }

  updateCartItemsFromHTML(html: string): void {
    const cartItemsContainer = document.querySelector('#offcanvas-cart .cart-items');

    if (cartItemsContainer) {
      cartItemsContainer.outerHTML = this.parseHtml(html, '.cart-items');
    }
  }

  updateCartTotalsFromHTML(html: string): void {
    const totalElement = document.querySelector('#offcanvas-cart .cart-total');

    if (totalElement) {
      totalElement.outerHTML = this.parseHtml(html, '.cart-total');
    }
  }

  updateFreeShippingFromHTML(html: string): void {
    const freeShippingElement = document.querySelector('#offcanvas-cart .cart-free-shipping');

    if (freeShippingElement) {
      freeShippingElement.outerHTML = this.parseHtml(html, '.cart-free-shipping');
    }
  }

  async updateCartCount(itemCount: number): Promise<void> {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach((element) => {
      element.textContent = itemCount.toString();
    });
  }

  showError(message: string): void {
    alert(message);
  }

  // Public method to refresh cart display - can be called by other modules
  async refreshCart(): Promise<CartResponse> {
    try {
      // Get current cart data
      const cart: CartResponse = await ky
        .get('/cart.js', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .json();
      await this.refreshCartDisplay(cart.item_count);
      this.applyCheckoutGate(cart.total_price, cart.item_count);

      return cart;
    } catch (error) {
      console.error('Error refreshing cart:', error);
      throw error;
    }
  }
}

export default Cart;
